import { Form } from 'antd'
import { useEffect, useMemo } from 'react'

import type { OrganizationRoleFull, PermissionsLocalized } from 'src/api/main'

import { getRoleInitialPermissions } from './utils'

export type RolesFormValues = {
  roles: {
    [roleId: number]: {
      [moduleResource: string]: number[]
    }
  }
}

/**
 * Build a form instance whose initial values mirror the per-role permission
 * map: `{ roles: { [roleId]: { [module]: [permissionIds] } } }`. Re-syncs when
 * either input changes so newly fetched roles or permission sets show up.
 */
export const usePermissionsForm = (
  roles: OrganizationRoleFull[],
  groupedPermissions: Record<string, PermissionsLocalized[]>
) => {
  const [form] = Form.useForm<RolesFormValues>()

  const initialValues = useMemo<RolesFormValues>(
    () => ({
      roles: Object.fromEntries(
        roles.map((role) => [
          role.id,
          getRoleInitialPermissions(role?.permissions, groupedPermissions)
        ])
      )
    }),
    [roles, groupedPermissions]
  )

  useEffect(() => {
    form.setFieldsValue(initialValues)
  }, [form, initialValues])

  return { form, initialValues }
}
